import useNonInitialEffect from '@versiondos/hooks';
import { BookingWrapper } from '@spike/booking-model';
import { FunctionComponent, useRef, useState } from 'react';
import { useApiClientWrapper, useMarketplace, useTimeZone } from 'hooks';
import moment from 'moment';
import ExportCSV from 'components/UI/ExportCSV/ExportCSV';
import { AppointmentListItem } from '@spike/appointments-list-model';
import styles from './AppointmentsExportCSV.module.scss';
import { Tooltip } from 'components/UI/V3/Tooltip/Tooltip';
import AppointmentsExportCSVModal from './AppointmentsExportCSVModal';
import { appointmentMatchesSelectedExportCategories } from './exportServiceCategories';

interface AppointmentsExportCSVProps {
    appointments: Array<AppointmentListItem>;
    selectedMarketplaceIds?: Array<number>;
    disabled?: boolean;
}

interface ExportingState {
    headers: string[];
    data: string[][];
}

const csvHeaders = [
    'Client Name',
    'Client Email',
    'Pet Name',
    'Breed',
    'Type',
    'Service',
    'Staff / Room',
    'Date & Time',
    'Price',
    'Status'
];

const AppointmentsExportCSV: FunctionComponent<AppointmentsExportCSVProps> = props => {
    const marketplace = useMarketplace();
    const timeZone = useTimeZone();

    const [exportingData, setExportingData] = useState<ExportingState>({
        headers: [],
        data: []
    });

    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);

    const csvRef = useRef<any>(null);

    useNonInitialEffect(() => {
        if (exportingData.data && exportingData.data.length > 0) {
            setTimeout(() => {
                csvRef.current?.export();
                setLoading(false);
            }, 1000);
        }
    }, [exportingData]);

    const exportHandler = (serviceCategories: Array<string>) => {
        hideModalHandler();
        setLoading(true);

        const csvData = props.appointments
            .filter(app => appointmentMatchesSelectedExportCategories(app.service.businessArea, serviceCategories))
            .map(app => [
                app.customer.firstName + ' ' + app.customer.lastName,
                app.customer.email.toLowerCase().indexOf('temporal.cuddlesapp.com') === -1 ? app.customer.email : '',
                app.pet.name,
                app.pet.breed?.name || '',
                app.pet.petType.singular,
                app.service.name,
                ['daycare', 'boarding'].includes(app.service.businessArea)
                    ? app.room?.name ?? ''
                    : app.staff?.name ?? 'Unassigned',
                app.service.businessArea === 'daycare'
                    ? `Drop-off: ${app.startAt.clone().tz(timeZone).format('YYYY-MM-DD HH:mm')} - Pick-up: ${app.endAt
                          .clone()
                          .tz(timeZone)
                          .format('HH:mm')}`
                    : app.service.businessArea === 'boarding'
                    ? `Check-in: ${app.startAt.clone().tz(timeZone).format('YYYY-MM-DD HH:mm')} - Check-out: ${app.endAt
                          .clone()
                          .tz(timeZone)
                          .format('HH:mm')}`
                    : app.startAt.clone().tz(timeZone).format('YYYY-MM-DD HH:mm'),
                app.total,
                app.status.name
            ]);

        setExportingData({
            headers: csvHeaders,
            data: csvData
        });
    };

    const showModalHandler = () => {
        setShowModal(true);
    };

    const hideModalHandler = () => {
        setShowModal(false);
    };

    const getFileName = () => {
        const date = moment().format('YYYYMMDD_HHmm');
        return `Appointments_Export_${marketplace.basics.businessName.replace(/\s+/g, '')}_${date}.csv`;
    };

    const exportButton = (
        <ExportCSV
            ref={csvRef}
            headers={exportingData.headers}
            records={exportingData.data}
            filename={getFileName()}
            exporting={loading}
            buttonClassName={styles.button}
            disabled={props.disabled}
            onClick={showModalHandler}
        />
    );

    return (
        <>
            {props.disabled ? (
                <Tooltip
                    content={<span>No bookings available to export.</span>}
                    placement="bottom"
                    className={styles.customTooltip}
                >
                    {exportButton}
                </Tooltip>
            ) : (
                exportButton
            )}
            {showModal && (
                <AppointmentsExportCSVModal
                    selectedMarketplaceIds={props.selectedMarketplaceIds}
                    onClose={hideModalHandler}
                    onExport={exportHandler}
                />
            )}
        </>
    );
};

export default AppointmentsExportCSV;
